import { Component, OnDestroy, signal, computed, effect, untracked } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

// Services

import { MainViewService } from '@core/services/main-view.service';
import { ModuleService } from '@core/services/module.service';
import { SessionService } from '@core/services/session.service';
import { SidebarService } from '@core/services/layouts/sidebar.service';
import { ImageService } from '@core/services/image.service';

// Model
import { MenuItem } from '@core/models/menu.model';

// Utils
import { Dic } from '@core/utils/dic.utils';

// PrimeNG
import { PanelMenuModule } from 'primeng/panelmenu';
import { MenuItem as PrimeMenuItem } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { LucideAngularModule, LucideChevronRight, LucidePanelLeft } from 'lucide-angular';

/**
 * Sidebar de navigation — charge le menu via SidebarService, filtre par recherche,
 * et délègue les clics à MainViewService (vue ou composant) ou ModuleService (dialog).
 * En mode collapsed, affiche les icônes uniquement avec flyout de sous-menus au survol.
 */
@Component({
	selector: 'layout-sidebar',
	imports: [NgTemplateOutlet, LucideAngularModule, PanelMenuModule, InputTextModule, IconFieldModule, InputIconModule],
	templateUrl: './sidebar.html',
	styleUrl: './sidebar.scss',
})
export class SidebarLayout implements OnDestroy {
	protected readonly Dic = Dic;

	panelMenuItems = signal<PrimeMenuItem[]>([]);
	searchQuery = signal<string>('');

	clubIcon = signal('');
	clubName = signal('');

	private activeMenuId = signal<number | undefined>(undefined);

	readonly railItems = computed(() => this.panelMenuItems().filter((item) => !item.styleClass?.includes('sidebar-section-label')));

	flyoutItem = signal<PrimeMenuItem | null>(null);
	flyoutPos = signal<{ top: number; left: number; maxHeight: number } | null>(null);
	flyoutExpandedIds = signal<ReadonlySet<string>>(new Set());
	railTip = signal<{ label: string; top: number; left: number } | null>(null);

	readonly lucidePanelLeft = LucidePanelLeft;
	readonly lucideChevronRight = LucideChevronRight;

	searchLabel = '';

	private rawMenuItems = signal<MenuItem[]>([]);

	private expandedIds = new Set<string>();
	private searchTimeout?: ReturnType<typeof setTimeout>;
	private dismissMouseDown?: (e: MouseEvent) => void;
	private dismissKeyDown?: (e: KeyboardEvent) => void;

	constructor(
		private mainView: MainViewService,
		private moduleService: ModuleService,
		private session: SessionService,
		private imageService: ImageService,
		protected sidebar: SidebarService,
	) {
		this.searchLabel = Dic.select.text;

		effect(() => {
			const rawItems = this.rawMenuItems() ?? [];
			if (this.session.data() && rawItems.length === 0) {
				this.loadMenu();
			}
		});

		effect(() => {
			const items = this.rawMenuItems() ?? [];
			const query = this.searchQuery();
			this.activeMenuId(); // dépendance pour le marquage actif
			untracked(() => this.syncExpandedIds(this.panelMenuItems()));
			if (items.length > 0) {
				const filtered = query ? this.filterMenuItems(items, query) : items;
				this.panelMenuItems.set(this.transformToPanelMenu(filtered));
			} else {
				this.panelMenuItems.set([]);
			}
		});

		effect(() => {
			const data = this.session.data();
			if (data) {
				this.clubIcon.set(this.imageService.getAppIcon(data.nomdt ?? 0));
				this.clubName.set(data.topbartitle || data.mandant || '');
			}
		});
	}

	ngOnDestroy(): void {
		clearTimeout(this.searchTimeout);
		this.removeDismissListeners();
	}

	onSearch(event: Event): void {
		clearTimeout(this.searchTimeout);
		const value = (event.target as HTMLInputElement).value.toLowerCase();
		this.searchTimeout = setTimeout(() => this.searchQuery.set(value), 300);
	}

	// ----- Rail et flyout (mode réduit)

	private static readonly FLYOUT_MARGIN = 16;
	private static readonly FLYOUT_MIN_HEIGHT = 320;
	private static readonly FLYOUT_HEIGHT_RATIO = 0.7;

	onRailClick(item: PrimeMenuItem, event: MouseEvent): void {
		if (!item.items?.length) {
			this.closeFlyout();
			item.command?.({ originalEvent: event, item });
			return;
		}
		if (this.flyoutItem() === item) {
			this.closeFlyout();
			return;
		}
		this.openFlyout(item, event.currentTarget as HTMLElement);
	}

	private openFlyout(item: PrimeMenuItem, anchor: HTMLElement): void {
		const rect = anchor.getBoundingClientRect();
		const rail = anchor.closest('.sidebar')?.getBoundingClientRect();
		const margin = SidebarLayout.FLYOUT_MARGIN;
		const viewport = window.innerHeight;
		// La carte s'aligne sur l'item, sans jamais descendre au point de passer sous FLYOUT_MIN_HEIGHT.
		const top = Math.max(margin, Math.min(rect.top, viewport - margin - SidebarLayout.FLYOUT_MIN_HEIGHT));
		this.flyoutPos.set({
			top,
			left: (rail?.right ?? rect.right) + 12,
			// Bornée par la place restante : la carte scrolle au lieu de déborder sous l'écran.
			maxHeight: Math.min(viewport * SidebarLayout.FLYOUT_HEIGHT_RATIO, viewport - top - margin),
		});
		this.flyoutItem.set(item);
		this.flyoutExpandedIds.set(new Set());
		this.railTip.set(null);
		this.registerDismissListeners();
	}

	closeFlyout(): void {
		this.removeDismissListeners();
		this.flyoutItem.set(null);
		this.flyoutPos.set(null);
	}

	toggleFlyoutSub(id: string): void {
		this.flyoutExpandedIds.update((ids) => {
			const next = new Set(ids);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	onFlyoutItemClick(item: PrimeMenuItem, event: Event): void {
		this.closeFlyout();
		item.command?.({ originalEvent: event, item });
	}

	showRailTip(event: MouseEvent, label: string | undefined): void {
		if (!label || this.flyoutItem()) return;
		const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
		this.railTip.set({ label, top: rect.top + rect.height / 2, left: rect.right + 10 });
	}

	hideRailTip(): void {
		this.railTip.set(null);
	}

	private registerDismissListeners(): void {
		this.removeDismissListeners();
		setTimeout(() => {
			this.dismissMouseDown = () => this.closeFlyout();
			this.dismissKeyDown = (e) => {
				if (e.key === 'Escape') this.closeFlyout();
			};
			document.addEventListener('mousedown', this.dismissMouseDown);
			document.addEventListener('keydown', this.dismissKeyDown);
		}, 0);
	}

	private removeDismissListeners(): void {
		if (this.dismissMouseDown) document.removeEventListener('mousedown', this.dismissMouseDown);
		if (this.dismissKeyDown) document.removeEventListener('keydown', this.dismissKeyDown);
		this.dismissMouseDown = undefined;
		this.dismissKeyDown = undefined;
	}

	// ----- Menu

	private filterMenuItems(items: MenuItem[], query: string): MenuItem[] {
		return items.reduce<MenuItem[]>((acc, item) => {
			const filteredChildren = item.menu?.items?.length ? this.filterMenuItems(item.menu.items, query) : [];
			const matchesText = item.text.toLowerCase().includes(query);
			if (matchesText || filteredChildren.length > 0) {
				acc.push(filteredChildren.length > 0 ? { ...item, menu: { cls: item.menu?.cls ?? '', items: filteredChildren } } : item);
			}
			return acc;
		}, []);
	}

	private loadMenu(): void {
		this.sidebar.getMenu(window.innerWidth < 1024).subscribe({
			next: (menuData) => {
				const items = menuData.items ?? [];
				if (menuData.mainmenuselected) this.expandedIds.add(String(menuData.mainmenuselected));
				this.sidebar.applyServerState(menuData.showmainmenu);
				this.rawMenuItems.set(items);
				this.openLastModule(items, menuData.mainmenuselected);
			},
			error: (err) => {
				console.error('Erreur lors du chargement du menu:', err);
				this.initializeFallbackMenu();
				this.moduleService.openDefaultModule();
			},
		});
	}

	private openLastModule(items: MenuItem[], menuId: number | undefined): void {
		const item = menuId ? this.findItemByMenuId(items, menuId) : null;
		if (item) this.onMenuClick(item);

		this.moduleService.openDefaultModule();
	}

	private findItemByMenuId(items: MenuItem[], menuId: number): MenuItem | null {
		for (const item of items) {
			if (item.menuid === menuId) return item;
			if (item.menu?.items) {
				const found = this.findItemByMenuId(item.menu.items, menuId);
				if (found) return found;
			}
		}
		return null;
	}

	private selectMenu(menuId: number): void {
		this.sidebar.selectMenu(menuId).subscribe({
			error: (err) => console.error('Erreur selectMenu:', err),
		});
	}

	private transformToPanelMenu(items: MenuItem[]): PrimeMenuItem[] {
		return items.map((item) => {
			const id = String(item.menuid);

			if (!item.iconCls && !item.xtype && !item.viewname && !item.menu?.items?.length) {
				return {
					id,
					label: item.text,
					styleClass: 'sidebar-section-label',
					disabled: true,
				};
			}

			const primeItem: PrimeMenuItem = {
				id,
				label: item.text,
				icon: this.convertIconToPrimeNG(item.iconCls),
			};
			if (item.menu?.items?.length) {
				const hasActiveDescendant = this.hasActiveDescendant(item);
				primeItem.items = this.transformToPanelMenu(item.menu.items);
				if (this.expandedIds.has(id) || hasActiveDescendant) {
					primeItem.expanded = true;
				}
				if (hasActiveDescendant) {
					primeItem.styleClass = 'active-menu-branch';
				}
			} else {
				primeItem.command = () => this.onMenuClick(item);
				if (item.menuid === this.activeMenuId()) primeItem.styleClass = 'active-menu-item';
			}
			if (item.alert > 0) primeItem.badge = item.alert.toString();
			return primeItem;
		});
	}

	private syncExpandedIds(items: PrimeMenuItem[]): void {
		for (const item of items) {
			if (!item.items?.length) continue;
			if (item.id) {
				if (item.expanded) this.expandedIds.add(item.id);
				else this.expandedIds.delete(item.id);
			}
			this.syncExpandedIds(item.items);
		}
	}

	private hasActiveDescendant(item: MenuItem): boolean {
		const activeId = this.activeMenuId();
		if (activeId === undefined) return false;
		return item.menu?.items?.some((child) => child.menuid === activeId || this.hasActiveDescendant(child)) ?? false;
	}

	private convertIconToPrimeNG(iconCls: string): string {
		return iconCls.replace('x-fa', 'fa').replace(/-o(\s|$)/g, '$1');
	}

	private initializeFallbackMenu(): void {
		this.panelMenuItems.set([{ label: 'Accueil', icon: 'pi pi-home', command: () => {} }]);
	}

	private onMenuClick(item: MenuItem): void {
		this.activeMenuId.set(item.menuid);
		if ('viewname' in item && item.viewname) {
			this.mainView.setView(item);
		} else if (item.xtype) {
			this.moduleService.openModule(item.xtype, item.params ?? {});
		}
		this.selectMenu(item.menuid);
		if (window.innerWidth < 1024) setTimeout(() => this.sidebar.close(), 100);
	}
}
